import React from 'react'
import { Card } from 'react-bootstrap'
import { StyledCard, StyledPerformanceCard } from '../styles'
import { Model } from '../../../../types/models/model'
import { useGetProjectQuery } from '../../../../services/projectsApi'

// Styled components now imported from ../styles

interface ModelMetrics {
  precision: number
  recall: number
  f1: number
  mAP: number
  accuracy: number
}

interface PerformanceCardProps {
  model: Model
  variant?: 'best' | 'worst'
  title?: string
  small?: boolean
}

const calculateModelMetrics = (model: Model): ModelMetrics => {
  const allowedMetrics = [
    'validation:mAP50-95',
    'validation:precision',
    'validation:recall',
    'validation:accuracy',
  ]
  let recall = 0
  let precision = 0
  let mAP = 0
  let accuracy = 0

  if (model.metrics) {
    Object.entries(model.metrics)
      .filter(([key]) => allowedMetrics.includes(key) && key.startsWith('validation:'))
      .forEach(([key, value]) => {
        const numericValue = typeof value === 'number' ? value : 0
        if (key.includes('precision')) {
          precision = numericValue
        }
        if (key.includes('recall')) {
          recall = numericValue
        }
        if (key.includes('mAP')) {
          mAP = numericValue
        }
        if (key.includes('accuracy')) {
          accuracy = numericValue
        }
      })
  }

  const f1 = (2 * (precision * recall)) / (precision + recall) || 0

  return {
    precision,
    recall,
    f1,
    mAP,
    accuracy,
  }
}

const PerformanceCard: React.FC<PerformanceCardProps> = ({ model, variant, title, small }) => {
  const metrics = calculateModelMetrics(model)
  const { data: projectData } = useGetProjectQuery(model.projectid)
  const isClassification = projectData?.annotation_type === 'classification'

  return (
    <StyledPerformanceCard $variant={variant} $small={small} $isClassification={isClassification}>
      {!small && title && <h3 className="card-title">{title}</h3>}
      <div className="performance-grid">
        {isClassification ? (
          <div className="metric-item">
            <p className="metric-value">{(metrics.accuracy * 100).toFixed(1)}%</p>
            <p className="metric-label">Accuracy</p>
          </div>
        ) : (
          <>
            <div className="metric-item">
              <p className="metric-value">{(metrics.precision * 100).toFixed(1)}%</p>
              <p className="metric-label">Precision</p>
            </div>
            <div className="metric-item">
              <p className="metric-value">{(metrics.recall * 100).toFixed(1)}%</p>
              <p className="metric-label">Recall</p>
            </div>
            <div className="metric-item">
              <p className="metric-value">{(metrics.f1 * 100).toFixed(1)}%</p>
              <p className="metric-label">F1 Score</p>
            </div>
            <div className="metric-item">
              <p className="metric-value">{(metrics.mAP * 100).toFixed(1)}%</p>
              <p className="metric-label">mAP</p>
            </div>
          </>
        )}
      </div>
    </StyledPerformanceCard>
  )
}

export default PerformanceCard
